import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { galleriesAPI, eventsAPI } from '../../lib/api';
import { useSEO } from '../../hooks/useSEO';
import { ArrowLeft, X, ChevronLeft, ChevronRight, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const getYoutubeEmbedUrl = (url) => {
  if (!url) return '';
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
  if (match) {
    return `https://www.youtube.com/embed/${match[1]}?autoplay=1`;
  }
  return url;
};

const getYoutubeThumbnail = (url) => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg` : null;
};

export const GalleryDetailPage = () => {
  const { slug } = useParams();
  const [gallery, setGallery] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxType, setLightboxType] = useState('image'); // 'image' or 'video'

  // Combine images and videos for lightbox navigation
  const allMedia = [
    ...(gallery?.images || []).map(img => ({ ...img, type: 'image' })),
    ...(gallery?.videos || []).map(vid => ({ ...vid, type: vid.type === 'youtube' ? 'youtube' : 'video' }))
  ];

  useSEO({
    title: gallery ? `${gallery.title} - Gallery` : 'Gallery',
    description: gallery?.description || 'Gallery fotografica',
    image: gallery?.hero_image,
  });

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const res = await galleriesAPI.getOne(slug);
        setGallery(res.data);
        
        // Load event info
        if (res.data.event_id) {
          const eventRes = await eventsAPI.getOne(res.data.event_id);
          setEvent(eventRes.data);
        }
      } catch (error) {
        console.error('Error loading gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    loadGallery();
  }, [slug]);

  const openLightbox = (index, type) => {
    // Find the correct index in allMedia
    let mediaIndex = 0;
    if (type === 'image') {
      mediaIndex = index;
    } else {
      mediaIndex = (gallery?.images?.length || 0) + index;
    }
    setLightboxIndex(mediaIndex);
    setLightboxType(allMedia[mediaIndex]?.type || 'image');
    setLightboxOpen(true);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < allMedia.length) {
      setLightboxIndex(newIndex);
      setLightboxType(allMedia[newIndex]?.type || 'image');
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxIndex]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sios-void flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-sios-void flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold mb-4">Gallery non trovata</h1>
          <Link to="/" className="text-primary hover:underline">Torna alla home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sios-void">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {gallery.hero_image && (
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${getFullUrl(gallery.hero_image)})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-sios-void/50 to-sios-void" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          {/* Back link */}
          {event && (
            <Link 
              to={`/events/${event.slug}`}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={16} />
              <span>Torna a {event.title}</span>
            </Link>
          )}
          
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4" data-testid="gallery-title">
            {gallery.title}
          </h1>
          
          {gallery.description && (
            <p className="text-lg text-muted-foreground max-w-2xl">
              {gallery.description}
            </p>
          )}
          
          <div className="flex items-center gap-4 mt-6 text-sm text-muted-foreground">
            <span>{gallery.images?.length || 0} immagini</span>
            {gallery.videos?.length > 0 && (
              <span>{gallery.videos.length} video</span>
            )}
          </div>
        </div>
      </section>

      {/* Masonry Gallery */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Images - Grid Layout */}
          {gallery.images && gallery.images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.images.map((image, index) => (
                <div 
                  key={`img-${index}`}
                  className="cursor-pointer group"
                  onClick={() => openLightbox(index, 'image')}
                  data-testid={`gallery-image-${index}`}
                >
                  <div className="relative overflow-hidden rounded-xl h-48 md:h-56 lg:h-64">
                    <img
                      src={getFullUrl(image.url)}
                      alt={image.caption || `Gallery image ${index + 1}`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    {/* Caption overlay on hover */}
                    {image.caption && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <p className="text-white text-sm font-medium line-clamp-2">{image.caption}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Videos Section */}
          {gallery.videos && gallery.videos.length > 0 && (
            <div className="mt-12">
              <h2 className="font-heading text-2xl font-bold mb-6">Video</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {gallery.videos.map((video, index) => (
                  <div 
                    key={`vid-${index}`}
                    className="cursor-pointer group"
                    onClick={() => openLightbox(index, 'video')}
                    data-testid={`gallery-video-${index}`}
                  >
                    <div className="relative aspect-video rounded-xl overflow-hidden">
                      {video.type === 'youtube' && getYoutubeThumbnail(video.url) ? (
                        <img
                          src={getYoutubeThumbnail(video.url)}
                          alt={video.caption || 'Video thumbnail'}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                          <Play size={48} className="text-primary" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                          <Play size={28} className="text-white ml-1" />
                        </div>
                      </div>
                    </div>
                    {video.caption && (
                      <p className="text-sm text-muted-foreground mt-2">{video.caption}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 md:p-8"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <X size={24} />
          </button>

          {/* Navigation */}
          {lightboxIndex > 0 && (
            <button 
              className="absolute left-4 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
            >
              <ChevronLeft size={24} />
            </button>
          )}
          {lightboxIndex < allMedia.length - 1 && (
            <button 
              className="absolute right-4 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
            >
              <ChevronRight size={24} />
            </button>
          )}

          {/* Content */}
          <div 
            className="w-full max-w-[90vw] max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {allMedia[lightboxIndex]?.type === 'image' ? (
              <img
                src={getFullUrl(allMedia[lightboxIndex].url)}
                alt={allMedia[lightboxIndex].caption || ''}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            ) : allMedia[lightboxIndex]?.type === 'youtube' ? (
              <div className="w-full max-w-[90vw] aspect-video">
                <iframe
                  src={getYoutubeEmbedUrl(allMedia[lightboxIndex].url)}
                  className="w-full h-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Video"
                />
              </div>
            ) : (
              <video 
                src={getFullUrl(allMedia[lightboxIndex].url)}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] rounded-lg"
              />
            )}
            
            {/* Caption */}
            {allMedia[lightboxIndex]?.caption && (
              <p className="text-center text-muted-foreground mt-4">
                {allMedia[lightboxIndex].caption}
              </p>
            )}
            
            {/* Counter */}
            <p className="text-center text-sm text-muted-foreground mt-2">
              {lightboxIndex + 1} / {allMedia.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
